import React, { useState } from 'react';
import axios from 'axios';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../../components/ui/dialog';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Card } from '../../components/ui/card';
import { toast } from 'sonner';
import {
  X, DollarSign, Plus, Trash2, User, FileText, Wallet,
  TrendingDown, TrendingUp, AlertTriangle, Wrench
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/**
 * Dashboard Financeiro de uma OF (modal grande).
 * Mostra: KPIs · Custos Pontuais editáveis · Arranjos/Defeitos com €/peça editável · Tabela de Operações.
 *
 * Props:
 *   open, onClose, of (objeto OF do /financeiro/analise-completa),
 *   config (configurações financeiras com taxa_hora_operador),
 *   getQtdFinal, formatCurrency, formatTempo, getDesvioIcon, getEficienciaColor (helpers do parent),
 *   CustoUnitarioInput (componente injetado),
 *   onCustosChanged (callback após adicionar/remover custos).
 */
export default function OfFinanceiroDashboard({
  open, onClose, of, config,
  getQtdFinal, formatCurrency, formatTempo, getDesvioIcon, getEficienciaColor,
  CustoUnitarioInput,
  onCustosChanged,
  focoCustos,
  qtdFinalAtual, onQtdFinalChange, onQtdFinalSave,
}) {
  const [novoCusto, setNovoCusto] = useState({ descricao: '', valor: '', tipo: 'outros' });
  const [savingCusto, setSavingCusto] = useState(false);

  if (!of) return null;

  const handleAddCusto = async () => {
    const descricao = (novoCusto.descricao || '').trim();
    const valor = parseFloat(novoCusto.valor);
    if (!descricao) { toast.error('Indique a descrição do custo'); return; }
    if (isNaN(valor) || valor <= 0) { toast.error('Indique um valor positivo'); return; }
    setSavingCusto(true);
    try {
      await axios.post(`${API}/ofs/${of.of_id}/custos-adicionais`, {
        descricao,
        valor,
        tipo: novoCusto.tipo || 'outros',
      });
      toast.success('Custo adicionado');
      setNovoCusto({ descricao: '', valor: '', tipo: 'outros' });
      if (onCustosChanged) await onCustosChanged();
    } catch (e) {
      toast.error('Erro ao adicionar custo: ' + (e.response?.data?.detail || e.message));
    } finally {
      setSavingCusto(false);
    }
  };

  const handleRemoveCusto = async (custoId) => {
    if (!custoId) return;
    if (!window.confirm('Remover este custo?')) return;
    try {
      await axios.delete(`${API}/custos-adicionais/${custoId}`);
      toast.success('Custo removido');
      if (onCustosChanged) await onCustosChanged();
    } catch (e) {
      toast.error('Erro ao remover: ' + (e.response?.data?.detail || e.message));
    }
  };

  const opsRelevantes = (of.operacoes || []).filter(op =>
    (op.pecas_total || 0) > 0 || (op.tempo_real_segundos || 0) > 0 || (op.operadores && op.operadores.length > 0)
  );
  const qtdFinal = getQtdFinal(of) || 0;
  const custoEstimado = (of.tempo_previsto_segundos / 3600) * (config?.taxa_hora_operador || 0);

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-7xl w-[97vw] max-h-[92vh] overflow-y-auto p-0" data-testid="of-dashboard-dialog">
        {/* Header com banda */}
        <div className="bg-gradient-to-r from-slate-900 to-slate-700 text-white p-5 rounded-t-lg">
          <div className="flex items-start justify-between gap-4">
            <div>
              <DialogHeader className="space-y-1">
                <DialogTitle className="text-2xl font-bold flex items-center gap-3 text-white">
                  <Wallet className="h-6 w-6" />
                  Dashboard Financeiro · {of.numero_of}
                  <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${
                    of.status === 'em_curso' ? 'bg-blue-500/30 text-blue-100' :
                    of.status === 'terminado' ? 'bg-emerald-500/30 text-emerald-100' :
                    'bg-stone-500/30 text-stone-100'
                  }`}>{of.status}</span>
                </DialogTitle>
                <DialogDescription className="text-slate-300">
                  {of.cliente || '—'} · {of.artigo || '—'} {of.modelo ? `· ${of.modelo}` : ''}
                </DialogDescription>
              </DialogHeader>
            </div>
            <button onClick={onClose} className="text-white/70 hover:text-white p-2 rounded hover:bg-white/10" aria-label="Fechar" data-testid="of-dashboard-close">
              <X className="h-5 w-5" />
            </button>
          </div>
        </div>

        <div className="p-5 space-y-5 bg-stone-50">
          {/* === Quantidade Final editável === */}
          <Card className="p-4 bg-white">
            <div className="flex flex-wrap items-center justify-between gap-4">
              <div>
                <p className="text-[10px] uppercase font-bold text-stone-500">Quantidade do Lote</p>
                <p className="text-2xl font-bold text-stone-700">{of.quantidade_total}</p>
              </div>
              <div className="text-stone-300 text-xl">→</div>
              <div className="flex-1 min-w-[200px]">
                <p className="text-[10px] uppercase font-bold text-blue-600 mb-1">Quantidade Final (real)</p>
                <div className="flex items-center gap-2">
                  <input
                    type="number"
                    min={0}
                    value={qtdFinalAtual ?? ''}
                    onChange={(e) => {
                      const val = e.target.value === '' ? '' : Number(e.target.value);
                      if (onQtdFinalChange) onQtdFinalChange(val);
                    }}
                    onFocus={(e) => e.target.select()}
                    onBlur={(e) => {
                      const val = Number(e.target.value);
                      if (val > 0 && onQtdFinalSave) onQtdFinalSave(val);
                    }}
                    onKeyDown={(e) => { if (e.key === 'Enter') e.target.blur(); }}
                    className="w-32 h-11 text-2xl font-bold text-blue-700 border-2 border-blue-300 rounded-md px-3 focus:border-blue-500 focus:ring-2 focus:ring-blue-300 outline-none bg-blue-50"
                    data-testid="of-dashboard-qtd-final-input"
                  />
                  <span className="text-sm text-stone-500">peças</span>
                </div>
                <p className="text-[10px] text-stone-400 mt-1">Edite para ajustar a qty real produzida (recalcula custo/peça e KPIs).</p>
              </div>
            </div>
          </Card>

          {/* === KPIs === */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <Card className="p-3 bg-white">
              <p className="text-[10px] uppercase font-bold text-stone-500">Custo Estimado</p>
              <p className="text-2xl font-bold text-stone-700">{formatCurrency(custoEstimado)}</p>
              <p className="text-[10px] text-stone-400 mt-0.5">tempo previsto × taxa/h</p>
            </Card>
            <Card className="p-3 bg-white">
              <p className="text-[10px] uppercase font-bold text-stone-500">Custo Real</p>
              <p className="text-2xl font-bold text-navy">{formatCurrency(of.custo_total)}</p>
              {of.variacao_custo !== 0 && of.custo_total > 0 && (
                <p className={`text-[11px] font-semibold flex items-center gap-1 mt-0.5 ${of.variacao_custo > 0 ? 'text-red-500' : 'text-emerald-600'}`}>
                  {of.variacao_custo > 0 ? <TrendingUp className="h-3 w-3" /> : <TrendingDown className="h-3 w-3" />}
                  {of.variacao_custo > 0 ? '+' : ''}{formatCurrency(of.variacao_custo)}
                </p>
              )}
            </Card>
            <Card className="p-3 bg-white">
              <p className="text-[10px] uppercase font-bold text-stone-500">Custo / Peça</p>
              <p className="text-2xl font-bold text-stone-700">
                {qtdFinal > 0 && of.custo_total > 0 ? formatCurrency(of.custo_total / qtdFinal) : (of.custo_total > 0 ? formatCurrency(of.custo_total) : '-')}
              </p>
              <p className="text-[10px] text-stone-400 mt-0.5">{qtdFinal} peças</p>
            </Card>
            <Card className="p-3 bg-white">
              <p className="text-[10px] uppercase font-bold text-stone-500">Eficiência</p>
              <p className={`text-2xl font-bold ${getEficienciaColor ? getEficienciaColor(of.eficiencia_percentual) : 'text-stone-700'}`}>
                {of.tempo_real_segundos > 0 ? `${of.eficiencia_percentual}%` : '—'}
              </p>
              <p className="text-[10px] text-stone-400 mt-0.5">{formatTempo(of.tempo_previsto_segundos)} / {formatTempo(of.tempo_real_segundos)}</p>
            </Card>
          </div>

          {/* === Custos Pontuais e Anomalias === */}
          <Card className={`p-4 ${focoCustos ? 'ring-2 ring-blue-400' : ''}`} data-testid="of-dashboard-custos-section">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <AlertTriangle className="h-4 w-4 text-amber-600" />
                <h3 className="font-bold text-stone-800">Custos Adicionais e Anomalias</h3>
              </div>
              <span className="text-xs text-stone-500">
                Total: <strong className="text-red-600">-{formatCurrency((of.custo_anomalias || 0) + (of.custo_adicional_total || 0))}</strong>
              </span>
            </div>

            {/* Arranjos & Defeitos editáveis */}
            <div className="flex flex-wrap items-center gap-3 mb-3">
              {of.arranjos > 0 && CustoUnitarioInput && (
                <CustoUnitarioInput of={of} tipo="arranjo" cor="blue" onUpdated={onCustosChanged} />
              )}
              {of.defeitos > 0 && CustoUnitarioInput && (
                <CustoUnitarioInput of={of} tipo="defeito" cor="red" onUpdated={onCustosChanged} />
              )}
              {(!of.arranjos && !of.defeitos) && (
                <p className="text-xs text-stone-400 italic flex items-center gap-1.5"><Wrench className="h-3 w-3" /> Sem arranjos/defeitos registados nesta OF</p>
              )}
            </div>

            {/* Custos Pontuais lista */}
            <div className="border-t pt-3">
              <p className="text-[10px] uppercase font-bold text-stone-500 mb-2">Custos Pontuais</p>
              {(of.custos_adicionais || []).length === 0 ? (
                <p className="text-xs text-stone-400 italic">— nenhum custo pontual adicionado —</p>
              ) : (
                <div className="flex flex-wrap gap-2">
                  {of.custos_adicionais.map((c) => (
                    <span key={c.id || `${c.descricao}-${c.valor}`} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-stone-100 border border-stone-200 text-sm" data-testid={`custo-pontual-${c.id || c.descricao}`}>
                      <span className="text-stone-700 font-medium">{c.descricao}</span>
                      <span className="text-red-600 font-bold">-{formatCurrency(c.valor)}</span>
                      <button onClick={() => handleRemoveCusto(c.id)} className="text-stone-400 hover:text-red-600 ml-1" title="Remover">
                        <Trash2 className="h-3.5 w-3.5" />
                      </button>
                    </span>
                  ))}
                </div>
              )}

              {/* Form Adicionar */}
              <div className="mt-3 grid grid-cols-1 md:grid-cols-12 gap-2 items-end">
                <div className="md:col-span-6">
                  <label className="text-[10px] uppercase font-bold text-stone-500 block mb-1">Descrição</label>
                  <Input
                    value={novoCusto.descricao}
                    onChange={(e) => setNovoCusto({ ...novoCusto, descricao: e.target.value })}
                    placeholder="Ex: Fio especial, frete, limpeza máquina…"
                    className="h-9 text-sm"
                    data-testid="custo-pontual-descricao"
                  />
                </div>
                <div className="md:col-span-2">
                  <label className="text-[10px] uppercase font-bold text-stone-500 block mb-1">Valor (€)</label>
                  <Input
                    type="number" step="0.01" min="0"
                    value={novoCusto.valor}
                    onChange={(e) => setNovoCusto({ ...novoCusto, valor: e.target.value })}
                    onFocus={(e) => e.target.select()}
                    placeholder="0,00"
                    className="h-9 text-sm"
                    data-testid="custo-pontual-valor"
                  />
                </div>
                <div className="md:col-span-2">
                  <label className="text-[10px] uppercase font-bold text-stone-500 block mb-1">Tipo</label>
                  <select
                    value={novoCusto.tipo}
                    onChange={(e) => setNovoCusto({ ...novoCusto, tipo: e.target.value })}
                    className="h-9 w-full rounded-md border border-stone-200 px-2 text-sm"
                    data-testid="custo-pontual-tipo"
                  >
                    <option value="outros">Outros</option>
                    <option value="material">Material</option>
                    <option value="servico">Serviço</option>
                    <option value="logistica">Logística</option>
                    <option value="manutencao">Manutenção</option>
                  </select>
                </div>
                <div className="md:col-span-2">
                  <Button
                    onClick={handleAddCusto}
                    disabled={savingCusto || !novoCusto.descricao.trim() || !novoCusto.valor}
                    className="h-9 w-full bg-blue-600 hover:bg-blue-700 text-white text-sm"
                    data-testid="custo-pontual-add"
                  >
                    <Plus className="h-4 w-4 mr-1" /> Adicionar
                  </Button>
                </div>
              </div>
            </div>
          </Card>

          {/* === Tabela de Operações === */}
          <Card className="overflow-hidden">
            <div className="px-4 py-3 border-b bg-white flex items-center justify-between">
              <div className="flex items-center gap-2">
                <FileText className="h-4 w-4 text-stone-500" />
                <h3 className="font-bold text-stone-800">Operações & Operadores</h3>
              </div>
              <span className="text-xs text-stone-500">{opsRelevantes.length} operação(ões) com produção</span>
            </div>
            <div className="overflow-x-auto bg-white">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-stone-500 border-b bg-stone-50">
                    <th className="py-2 px-3 font-medium">Operação</th>
                    <th className="py-2 px-3 font-medium">Operador(es)</th>
                    <th className="py-2 px-3 font-medium text-center">Tempo Prev</th>
                    <th className="py-2 px-3 font-medium text-center">Tempo Real</th>
                    <th className="py-2 px-3 font-medium text-center">Desvio</th>
                    <th className="py-2 px-3 font-medium text-center">Eficiência</th>
                    <th className="py-2 px-3 font-medium text-right">Custo Est.</th>
                    <th className="py-2 px-3 font-medium text-right">Custo Real</th>
                    <th className="py-2 px-3 font-medium text-right">Custo/Pç</th>
                    <th className="py-2 px-3 font-medium text-center">Peças</th>
                  </tr>
                </thead>
                <tbody>
                  {opsRelevantes.length === 0 ? (
                    <tr><td colSpan={10} className="py-10 text-center text-stone-400">
                      Sem registos de produção para esta OF
                    </td></tr>
                  ) : (
                    opsRelevantes.map((op, idx) => (
                      <tr key={op.operacao_id || `${op.operacao_codigo}-${idx}`} className="border-b border-stone-100 last:border-0 hover:bg-stone-50">
                        <td className="py-2.5 px-3">
                          <div className="flex items-center gap-2">
                            {getDesvioIcon ? getDesvioIcon(op.status) : null}
                            <div>
                              <p className="font-medium text-stone-800">{op.operacao_nome}</p>
                              {op.operacao_codigo && <p className="text-[10px] text-stone-400">{op.operacao_codigo}</p>}
                            </div>
                          </div>
                        </td>
                        <td className="py-2.5 px-3">
                          {op.operadores && op.operadores.length > 0 ? (
                            <div className="flex flex-wrap gap-1">
                              {op.operadores.map((operador) => (
                                <span
                                  key={operador.id || `${op.operacao_id}-${operador.nome}`}
                                  className="inline-flex items-center gap-1 px-2 py-0.5 bg-blue-50 text-blue-700 rounded text-xs"
                                  title={`${operador.pecas} peças | ${formatTempo(operador.tempo_segundos)}`}
                                >
                                  <User className="h-3 w-3" />
                                  {operador.nome}
                                  <span className={`font-bold ${getEficienciaColor ? getEficienciaColor(operador.eficiencia) : ''}`}>({operador.eficiencia}%)</span>
                                </span>
                              ))}
                            </div>
                          ) : <span className="text-stone-300 text-xs">—</span>}
                        </td>
                        <td className="py-2.5 px-3 text-center text-stone-600">{formatTempo(op.tempo_previsto_segundos)}</td>
                        <td className="py-2.5 px-3 text-center text-stone-800 font-medium">{formatTempo(op.tempo_real_segundos)}</td>
                        <td className="py-2.5 px-3 text-center">
                          {op.tempo_real_segundos > 0 ? (
                            <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                              op.status === 'mais_rapido' ? 'bg-emerald-100 text-emerald-700' :
                              op.status === 'mais_lento' ? 'bg-red-100 text-red-700' : 'bg-stone-100 text-stone-600'
                            }`}>
                              {op.desvio_percentual > 0 ? '+' : ''}{op.desvio_percentual}%
                            </span>
                          ) : <span className="text-stone-300">—</span>}
                        </td>
                        <td className="py-2.5 px-3 text-center">
                          {op.tempo_real_segundos > 0 ? (
                            <span className={`font-bold ${getEficienciaColor ? getEficienciaColor(op.eficiencia_percentual) : ''}`}>{op.eficiencia_percentual}%</span>
                          ) : <span className="text-stone-300">—</span>}
                        </td>
                        <td className="py-2.5 px-3 text-right font-medium text-stone-500">
                          {formatCurrency((op.tempo_previsto_segundos / 3600) * (config?.taxa_hora_operador || 0))}
                        </td>
                        <td className="py-2.5 px-3 text-right font-medium text-stone-800">{formatCurrency(op.custo)}</td>
                        <td className="py-2.5 px-3 text-right font-medium text-stone-600">
                          {op.pecas_produzidas > 0 ? formatCurrency(op.custo_por_peca || (op.custo / op.pecas_produzidas)) : '—'}
                        </td>
                        <td className="py-2.5 px-3 text-center text-stone-600">{op.pecas_produzidas || op.pecas_total || 0}</td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </Card>
        </div>
      </DialogContent>
    </Dialog>
  );
}
